import { useChangeFilterTrack } from "@/_old/features/analytics";
import { useDataTable } from "@/core/components/layout/data-table/hooks/use-data-table";
import { useDebounce } from "@/core/utils/debounce";
import { DashboardFilters } from "@/dashboard/types";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DashboardRankingsCitiesColumns } from "../components/cities-data-table/columns";
import { getRankings } from "../services/rankings-service";
import { GetRankingsParams, GetRankingsResponse } from "../types";

const useDashboardRankingsCitiesSheet = (
  dashboardFilters: DashboardFilters,
) => {
  const { onChangeFilter } = useChangeFilterTrack();

  const [t] = useTranslation(["dashboard"]);
  const translations = useMemo(
    () => ({
      position: t("dashboard-rankings-cities-table-header-position"),
      name: t("dashboard-rankings-cities-table-header-name"),
      value: t("dashboard-rankings-cities-table-header-value"),
    }),
    [],
  );

  const columns = useMemo(
    () =>
      DashboardRankingsCitiesColumns({
        translations,
      }),
    [translations],
  );

  const { table, pagination, globalFilter, filters } = useDataTable({
    data: [],
    columns,
    pageCount: 1,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    enableRowSelection: false,
    enableMultiRowSelection: false,
  });

  const debouncedSearch = useDebounce(globalFilter, 300);
  const debouncedFilters = useDebounce(filters, 500);

  const debouncedParams = useMemo(() => {
    if (debouncedSearch) {
      onChangeFilter("FILTER_SEARCH", "DASHBOARD");
    }

    return {
      page:
        !debouncedSearch || debouncedFilters.length === 0
          ? pagination.pageIndex + 1
          : 1,
      search: debouncedSearch,
      limit: pagination.pageSize,
      rank_by: "bookings",
      ...dashboardFilters,
    };
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    debouncedSearch,
    debouncedFilters,
    dashboardFilters,
  ]);

  const {
    data: response,
    error,
    isLoading: isLoadingResponse,
  } = useQuery<GetRankingsResponse>({
    queryKey: ["dashboard-rankings-cities-sheet", debouncedParams],
    queryFn: async ({ signal }) =>
      await getRankings("city", debouncedParams as GetRankingsParams, signal),
  });

  const top3RankingData = useMemo(() => {
    return response?.data.slice(0, 3) || [];
  }, [response]);

  const otherRankingData = useMemo(() => {
    return (
      (!table.getState().globalFilter
        ? response?.data.slice(3)
        : response?.data) || []
    );
  }, [response, table.getState().globalFilter]);

  const pageCount = useMemo(() => response?.total_pages || 1, [response]);
  const total = useMemo(() => response?.total || 0, [response]);

  const isLoading = useMemo(() => {
    return isLoadingResponse || !response;
  }, [isLoadingResponse, response]);

  table.setOptions((opts) => ({
    ...opts,
    data: otherRankingData,
    pageCount,
  }));

  return {
    table,
    top3RankingData,
    otherRankingData,
    total,
    pageCount,
    searchParams: debouncedParams,
    error,
    isLoading,
    columns,
  };
};

export { useDashboardRankingsCitiesSheet };
